'use client';

/**
 * Product detail gallery — swipeable carousel + thumbnail strip + fullscreen
 * lightbox. Drop-in replacement for the prior `Gallery` component in
 * `ProductDetailClient.tsx`.
 *
 * Carousel: native CSS scroll-snap horizontal scroller. Free swipe physics
 * from the browser, no touch-event JS. An IntersectionObserver watches which
 * slide is most visible and reports the change up via `onSelect(url)`. When
 * `selectedImage` changes externally (e.g. variant swap), we scroll the
 * matching slide into view programmatically — and when it isn't part of the
 * gallery at all, it becomes a trailing slide (see `slides`).
 *
 * Lightbox: clicking any slide opens a portaled black overlay with the image
 * shown `object-contain`, prev/next arrows, keyboard arrows (RTL-aware), Esc
 * to close, and a thumbnail strip at the bottom.
 */
import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { ProductImagePlaceholder } from './ProductImagePlaceholder';
import { SafeImage } from './SafeImage';
import { IconButton } from './ui/icon-button';
import { OverlayCloseButton } from './ui/overlay-close-button';
import { Z } from '@/lib/z-index';
type GalleryImage = {
  url: string;
  display_order: number;
  blur_data_url?: string | null;
};
type Props = {
  images: GalleryImage[];
  /** Show the whole product (cut-outs): contain with padding, never crop. */
  contain?: boolean;
  selectedImage: string;
  onSelect: (url: string) => void;
  productName: string;
  locale: string;
  /**
   * `sizes` hint for the main carousel image. Defaults to the PDP value
   * (full viewport on mobile, ~560px on desktop, matching the outer wrapper's
   * max-w-[560px]). Override for tighter contexts like QuickView (where the
   * gallery only takes ~500px even at 1920px viewport).
   */
  mainImageSizes?: string;
  /** `sizes` hint for thumbnails. Override when thumbs render smaller/larger. */
  thumbnailSizes?: string;
  /**
   * Classes for the image frame — its aspect ratio and corner rounding.
   * The PDP wants a rounded square; the quick view's mobile sheet wants a
   * shorter, edge-to-edge image so the details below it stay in view.
   */
  frameClassName?: string;
  /** Classes for the outer column. Defaults to the PDP's centred 560px cap. */
  containerClassName?: string;
};
const DEFAULT_FRAME = 'aspect-square rounded-2xl';
export default function ProductGalleryCarousel(t0) {
  const $ = _c(81);
  const {
    images,
    selectedImage,
    onSelect,
    productName,
    locale,
    mainImageSizes: t1,
    thumbnailSizes: t2,
    frameClassName: t3,
    containerClassName: t4,
    contain: t5
  } = t0;
  const mainImageSizes = t1 === undefined ? "(max-width: 768px) 100vw, 560px" : t1;
  const thumbnailSizes = t2 === undefined ? "80px" : t2;
  const frameClassName = t3 === undefined ? DEFAULT_FRAME : t3;
  const containerClassName = t4 === undefined ? "max-w-[560px] w-full mx-auto" : t4;
  const contain = t5 === undefined ? false : t5;
  const fit = contain ? "object-contain p-6" : "object-cover";
  const isRTL = locale === "ar";
  const scrollerRef = useRef(null);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const slideRefs = useRef(t6);
  const programmaticScrollRef = useRef(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  let t7;
  bb0: {
    if (!selectedImage || images.some(img => img.url === selectedImage)) {
      t7 = images;
      break bb0;
    }
    let t8;
    if ($[1] !== images.length || $[2] !== selectedImage) {
      t8 = {
        url: selectedImage,
        display_order: images.length
      };
      $[1] = images.length;
      $[2] = selectedImage;
      $[3] = t8;
    } else {
      t8 = $[3];
    }
    let t9;
    if ($[4] !== images || $[5] !== t8) {
      t9 = [...images, t8];
      $[4] = images;
      $[5] = t8;
      $[6] = t9;
    } else {
      t9 = $[6];
    }
    t7 = t9;
  }
  const slides = t7;
  let t8;
  if ($[7] !== selectedImage) {
    t8 = img_0 => img_0.url === selectedImage;
    $[7] = selectedImage;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const i = slides.findIndex(t8);
  const currentIndex = i >= 0 ? i : 0;
  const nextMoveRef = useRef("fade");
  const shownUrlRef = useRef(selectedImage);
  const [fadeOverlay, setFadeOverlay] = useState(null);
  const isFirstRunRef = useRef(true);
  let t9;
  if ($[9] !== currentIndex || $[10] !== selectedImage || $[11] !== slides) {
    t9 = () => {
      const move = nextMoveRef.current;
      nextMoveRef.current = "fade";
      const isFirstRun = isFirstRunRef.current;
      isFirstRunRef.current = false;
      const previousUrl = shownUrlRef.current;
      shownUrlRef.current = slides[currentIndex]?.url ?? selectedImage;
      const target = slideRefs.current[currentIndex];
      const scroller = scrollerRef.current;
      if (!target || !scroller) {
        return;
      }
      const expected = target.offsetLeft;
      const drift = Math.abs(scroller.scrollLeft - expected);
      const newUrl = slides[currentIndex]?.url;
      const crossfade = !isFirstRun && move === "fade" && Boolean(previousUrl) && previousUrl !== newUrl;
      if (!crossfade) {
        setFadeOverlay(null);
      }
      if (drift < 4 && !crossfade) {
        return;
      }
      if (crossfade) {
        setFadeOverlay(o => o && !o.fading ? o : {
          url: previousUrl,
          fading: false
        });
      }
      programmaticScrollRef.current = true;
      target.scrollIntoView({
        behavior: isFirstRun || crossfade ? "instant" : "smooth",
        inline: "start",
        block: "nearest"
      });
      const img_1 = target.querySelector("img");
      let revealed = false;
      let giveUpTimer;
      const reveal = () => {
        if (revealed) {
          return;
        }
        revealed = true;
        window.clearTimeout(giveUpTimer);
        setFadeOverlay(_temp);
        window.setTimeout(() => setFadeOverlay(_temp2), 350);
      };
      if (crossfade) {
        if (!img_1 || img_1.complete && img_1.naturalWidth > 0) {
          reveal();
        } else {
          img_1.setAttribute("loading", "eager");
          img_1.addEventListener("load", reveal, {
            once: true
          });
          img_1.addEventListener("error", reveal, {
            once: true
          });
          giveUpTimer = window.setTimeout(reveal, 3000);
        }
      }
      const t = window.setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 450);
      return () => {
        window.clearTimeout(t);
        window.clearTimeout(giveUpTimer);
        img_1?.removeEventListener("load", reveal);
        img_1?.removeEventListener("error", reveal);
      };
    };
    $[9] = currentIndex;
    $[10] = selectedImage;
    $[11] = slides;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== currentIndex || $[14] !== selectedImage) {
    t10 = [currentIndex, selectedImage];
    $[13] = currentIndex;
    $[14] = selectedImage;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[16] !== onSelect || $[17] !== selectedImage || $[18] !== slides) {
    t11 = () => {
      const scroller_0 = scrollerRef.current;
      if (!scroller_0 || slides.length <= 1) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (programmaticScrollRef.current) {
          return;
        }
        const dominant = entries.filter(_temp3).sort(_temp4)[0];
        if (!dominant) {
          return;
        }
        const idx = slideRefs.current.indexOf(dominant.target as HTMLDivElement);
        if (idx < 0) {
          return;
        }
        const url = slides[idx]?.url;
        if (url && url !== selectedImage) {
          nextMoveRef.current = "slide";
          onSelect(url);
        }
      }, {
        root: scroller_0,
        threshold: [0.5, 0.75, 0.95]
      });
      slideRefs.current.forEach(el => el && observer.observe(el));
      return () => observer.disconnect();
    };
    t12 = [slides, onSelect, selectedImage];
    $[16] = onSelect;
    $[17] = selectedImage;
    $[18] = slides;
    $[19] = t11;
    $[20] = t12;
  } else {
    t11 = $[19];
    t12 = $[20];
  }
  useEffect(t11, t12);
  let t13;
  if ($[21] !== currentIndex || $[22] !== onSelect || $[23] !== slides) {
    t13 = function goPrev() {
      if (currentIndex <= 0) {
        return;
      }
      nextMoveRef.current = "slide";
      onSelect(slides[currentIndex - 1].url);
    };
    $[21] = currentIndex;
    $[22] = onSelect;
    $[23] = slides;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const goPrev = t13;
  let t14;
  if ($[25] !== currentIndex || $[26] !== onSelect || $[27] !== slides) {
    t14 = function goNext() {
      if (currentIndex >= slides.length - 1) {
        return;
      }
      nextMoveRef.current = "slide";
      onSelect(slides[currentIndex + 1].url);
    };
    $[25] = currentIndex;
    $[26] = onSelect;
    $[27] = slides;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const goNext = t14;
  if (slides.length === 0) {
    const t15 = `relative bg-subtle overflow-hidden ${frameClassName}`;
    let t16;
    if ($[29] !== productName) {
      t16 = <ProductImagePlaceholder alt={productName} />;
      $[29] = productName;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    let t17;
    if ($[31] !== t15 || $[32] !== t16) {
      t17 = <div className={t15}>{t16}</div>;
      $[31] = t15;
      $[32] = t16;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    let t18;
    if ($[34] !== containerClassName || $[35] !== t17) {
      t18 = <div className={containerClassName}>{t17}</div>;
      $[34] = containerClassName;
      $[35] = t17;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    return t18;
  }
  const t15 = `flex overflow-x-auto snap-x snap-mandatory scroll-smooth bg-subtle scrollbar-hide ${frameClassName}`;
  let t16;
  if ($[37] !== fit || $[38] !== isRTL || $[39] !== mainImageSizes || $[40] !== productName || $[41] !== slides) {
    t16 = slides.map((img_2, i_0) => <div key={img_2.url} ref={el_0 => {
      slideRefs.current[i_0] = el_0;
    }} data-slide-index={i_0} className="snap-start shrink-0 w-full h-full relative"><button type="button" onClick={() => setLightboxIndex(i_0)} className="absolute inset-0 cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset" aria-label={isRTL ? `تكبير الصورة ${i_0 + 1} من ${slides.length}` : `Zoom image ${i_0 + 1} of ${slides.length}`}><SafeImage src={img_2.url} alt={productName} fill={true} className={`${fit} pointer-events-none`} sizes={mainImageSizes} priority={i_0 === 0} blurDataURL={img_2.blur_data_url} fallback={<ProductImagePlaceholder alt={productName} />} /></button></div>);
    $[37] = fit;
    $[38] = isRTL;
    $[39] = mainImageSizes;
    $[40] = productName;
    $[41] = slides;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15 || $[44] !== t16) {
    t17 = <div ref={scrollerRef} className={t15}>{t16}</div>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== fadeOverlay || $[47] !== fit || $[48] !== frameClassName || $[49] !== mainImageSizes) {
    t18 = fadeOverlay && <div data-gallery-crossfade="" aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden bg-subtle transition-opacity duration-300 ease-out motion-reduce:transition-none ${frameClassName} ${fadeOverlay.fading ? "opacity-0" : "opacity-100"}`}><SafeImage src={fadeOverlay.url} alt="" fill={true} className={fit} sizes={mainImageSizes} fallback={<ProductImagePlaceholder />} /></div>;
    $[46] = fadeOverlay;
    $[47] = fit;
    $[48] = frameClassName;
    $[49] = mainImageSizes;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== currentIndex || $[52] !== goNext || $[53] !== goPrev || $[54] !== isRTL || $[55] !== slides.length) {
    t19 = slides.length > 1 && <><div className="hidden md:flex absolute inset-y-0 start-3 items-center pointer-events-none"><IconButton variant="soft" color="default" size="md" icon={isRTL ? <CaretRight weight="bold" /> : <CaretLeft weight="bold" />} onClick={goPrev} disabled={currentIndex === 0} aria-label={isRTL ? "\u0627\u0644\u0633\u0627\u0628\u0642" : "Previous"} className="pointer-events-auto bg-white/85 backdrop-blur-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0" /></div><div className="hidden md:flex absolute inset-y-0 end-3 items-center pointer-events-none"><IconButton variant="soft" color="default" size="md" icon={isRTL ? <CaretLeft weight="bold" /> : <CaretRight weight="bold" />} onClick={goNext} disabled={currentIndex >= slides.length - 1} aria-label={isRTL ? "\u0627\u0644\u062A\u0627\u0644\u064A" : "Next"} className="pointer-events-auto bg-white/85 backdrop-blur-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0" /></div></>;
    $[51] = currentIndex;
    $[52] = goNext;
    $[53] = goPrev;
    $[54] = isRTL;
    $[55] = slides.length;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== currentIndex || $[58] !== slides) {
    t20 = slides.length > 1 && <div className="md:hidden absolute bottom-3 inset-x-0 flex items-center justify-center gap-1.5 pointer-events-none" aria-hidden="true">{slides.map((img_3, i_1) => <span key={img_3.url} className={`h-1.5 rounded-full transition-all duration-200 ${i_1 === currentIndex ? "w-5 bg-white shadow" : "w-1.5 bg-white/60"}`} />)}</div>;
    $[57] = currentIndex;
    $[58] = slides;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== t17 || $[61] !== t18 || $[62] !== t19 || $[63] !== t20) {
    t21 = <div className="relative group">{t17}{t18}{t19}{t20}</div>;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  let t22;
  if ($[65] !== currentIndex || $[66] !== isRTL || $[67] !== onSelect || $[68] !== slides || $[69] !== thumbnailSizes) {
    t22 = slides.length > 1 && <div className="flex gap-2 mt-2 px-1 py-1 overflow-x-auto scroll-smooth scrollbar-hide">{slides.map((img_4, i_2) => {
        const isSelected = i_2 === currentIndex;
        return <button key={img_4.url} type="button" onClick={() => {
          nextMoveRef.current = "fade";
          onSelect(img_4.url);
        }} aria-label={`${isRTL ? "\u0635\u0648\u0631\u0629" : "Image"} ${i_2 + 1}`} aria-current={isSelected} className={["relative flex-1 min-w-0 max-w-20 aspect-square rounded-lg transition-all duration-150", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background", isSelected ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "opacity-70 hover:opacity-100"].join(" ")}><div className="absolute inset-0 rounded-lg overflow-hidden"><SafeImage src={img_4.url} alt="" fill={true} className="object-cover" sizes={thumbnailSizes} blurDataURL={img_4.blur_data_url} fallback={<ProductImagePlaceholder />} /></div></button>;
      })}</div>;
    $[65] = currentIndex;
    $[66] = isRTL;
    $[67] = onSelect;
    $[68] = slides;
    $[69] = thumbnailSizes;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== lightboxIndex || $[72] !== locale || $[73] !== productName || $[74] !== slides) {
    t23 = lightboxIndex !== null && <Lightbox images={slides} startIndex={lightboxIndex} productName={productName} locale={locale} onClose={() => setLightboxIndex(null)} />;
    $[71] = lightboxIndex;
    $[72] = locale;
    $[73] = productName;
    $[74] = slides;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] !== containerClassName || $[77] !== t21 || $[78] !== t22 || $[79] !== t23) {
    t24 = <div className={containerClassName}>{t21}{t22}{t23}</div>;
    $[76] = containerClassName;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  return t24;
}

// ────────────────────────────────────────────────────────────────────────────
// Lightbox — fullscreen image viewer with prev/next nav
// ────────────────────────────────────────────────────────────────────────────
function _temp4(a, b) {
  return b.intersectionRatio - a.intersectionRatio;
}
function _temp3(e) {
  return e.isIntersecting;
}
function _temp2(o_1) {
  return o_1?.fading ? null : o_1;
}
function _temp(o_0) {
  return o_0 ? {
    ...o_0,
    fading: true
  } : o_0;
}
function Lightbox(t0) {
  const $ = _c(45);
  const {
    images,
    startIndex,
    productName,
    locale,
    onClose
  } = t0;
  const [index, setIndex] = useState(startIndex);
  const isRTL = locale === "ar";
  let t1;
  let t2;
  if ($[0] !== images.length || $[1] !== isRTL || $[2] !== onClose) {
    t1 = () => {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const handleKey = function handleKey(e) {
        if (e.key === "Escape") {
          e.stopImmediatePropagation();
          onClose();
          return;
        }
        if (e.key === "ArrowLeft") {
          e.stopImmediatePropagation();
          setIndex(i => isRTL ? Math.min(images.length - 1, i + 1) : Math.max(0, i - 1));
        }
        if (e.key === "ArrowRight") {
          e.stopImmediatePropagation();
          setIndex(i_0 => isRTL ? Math.max(0, i_0 - 1) : Math.min(images.length - 1, i_0 + 1));
        }
      };
      window.addEventListener("keydown", handleKey, true);
      return () => {
        window.removeEventListener("keydown", handleKey, true);
        document.body.style.overflow = prevOverflow;
      };
    };
    t2 = [onClose, images.length, isRTL];
    $[0] = images.length;
    $[1] = isRTL;
    $[2] = onClose;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIndex(_temp5);
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const goPrev = t3;
  let t4;
  if ($[6] !== images.length) {
    t4 = () => setIndex(i_2 => Math.min(images.length - 1, i_2 + 1));
    $[6] = images.length;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const goNext = t4;
  if (typeof document === "undefined") {
    return null;
  }
  const trackOffset = (isRTL ? 1 : -1) * index * 100;
  const t5 = index + 1;
  let t6;
  if ($[8] !== images.length || $[9] !== t5) {
    t6 = <span className="text-sm text-white/80 tabular-nums px-2">{t5} / {images.length}</span>;
    $[8] = images.length;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onClose) {
    t7 = <OverlayCloseButton onClick={onClose} className="bg-white/10 text-white hover:bg-white/20" />;
    $[11] = onClose;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div className="flex items-center justify-between p-3 shrink-0">{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = `translateX(${trackOffset}%)`;
  let t10;
  if ($[16] !== t9) {
    t10 = {
      transform: t9
    };
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== images || $[19] !== index || $[20] !== productName) {
    let t12;
    if ($[22] !== index || $[23] !== productName) {
      t12 = (img, i_3) => <div key={img.url} className="relative w-full h-full shrink-0" aria-hidden={i_3 !== index}><SafeImage src={img.url} alt={i_3 === index ? productName : ""} fill={true} className="object-contain" sizes="100vw" blurDataURL={img.blur_data_url} priority={Math.abs(i_3 - index) <= 1} fallback={<ProductImagePlaceholder alt={i_3 === index ? productName : ""} />} /></div>;
      $[22] = index;
      $[23] = productName;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    t11 = images.map(t12);
    $[18] = images;
    $[19] = index;
    $[20] = productName;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className="flex w-full h-full transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform" style={t10}>{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== goNext || $[29] !== images.length || $[30] !== index || $[31] !== isRTL) {
    t13 = images.length > 1 && <><div className="absolute inset-y-0 start-2 md:start-6 flex items-center"><IconButton variant="soft" color="default" size="lg" icon={isRTL ? <CaretRight weight="bold" /> : <CaretLeft weight="bold" />} onClick={goPrev} disabled={index === 0} aria-label={isRTL ? "\u0627\u0644\u0633\u0627\u0628\u0642" : "Previous"} className="bg-white/15 text-white hover:bg-white/25 disabled:opacity-30" /></div><div className="absolute inset-y-0 end-2 md:end-6 flex items-center"><IconButton variant="soft" color="default" size="lg" icon={isRTL ? <CaretLeft weight="bold" /> : <CaretRight weight="bold" />} onClick={goNext} disabled={index >= images.length - 1} aria-label={isRTL ? "\u0627\u0644\u062A\u0627\u0644\u064A" : "Next"} className="bg-white/15 text-white hover:bg-white/25 disabled:opacity-30" /></div></>;
    $[28] = goNext;
    $[29] = images.length;
    $[30] = index;
    $[31] = isRTL;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = <div className="flex-1 relative overflow-hidden">{t12}{t13}</div>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== images || $[37] !== index || $[38] !== isRTL) {
    t15 = images.length > 1 && <div className="shrink-0 p-3 flex justify-center"><div className="flex gap-2 px-1 py-1 overflow-x-auto max-w-full scrollbar-hide">{images.map((img_0, i_4) => {
          const isSelected = i_4 === index;
          return <button key={img_0.url} type="button" onClick={() => setIndex(i_4)} aria-label={`${isRTL ? "\u0635\u0648\u0631\u0629" : "Image"} ${i_4 + 1}`} aria-current={isSelected} className={["relative h-14 w-14 rounded-lg shrink-0 transition-all duration-150", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black", isSelected ? "ring-2 ring-white ring-offset-2 ring-offset-black" : "opacity-60 hover:opacity-100"].join(" ")}><div className="absolute inset-0 rounded-lg overflow-hidden"><SafeImage src={img_0.url} alt="" fill={true} className="object-cover" sizes="56px" blurDataURL={img_0.blur_data_url} fallback={<ProductImagePlaceholder />} /></div></button>;
        })}</div></div>;
    $[36] = images;
    $[37] = index;
    $[38] = isRTL;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== productName || $[41] !== t14 || $[42] !== t15 || $[43] !== t8) {
    const content = <div className={`fixed inset-0 ${Z.OVERLAY} bg-black/95 flex flex-col`} role="dialog" aria-modal="true" aria-label={productName}>{t8}{t14}{t15}</div>;
    t16 = createPortal(content, document.body);
    $[40] = productName;
    $[41] = t14;
    $[42] = t15;
    $[43] = t8;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
}
function _temp5(i_1) {
  return Math.max(0, i_1 - 1);
}